import { createStore } from '@cascade/core/utils';
import { useLatest } from '@cascade/react/hooks';
import { useCallback, useEffect, useSyncExternalStore } from 'react';
import { getPlatform } from './usePwaInstall.helpers.ts';
import type {
  TBeforeInstallPromptEvent,
  TInstallPromptSnapshot,
  TPwaInstall,
  TPwaInstallOptions,
  TStandaloneNavigator,
} from './usePwaInstall.types.ts';

const STANDALONE_MEDIA_QUERY = '(display-mode: standalone)';

const installedListeners = new Set<() => void>();

const installPromptStore = createStore<TInstallPromptSnapshot>(
  { isInstalled: false },
  {
    onFirstSubscribe: () => {
      window.addEventListener('beforeinstallprompt', handleInstallPrompt);
      window.addEventListener('appinstalled', handleInstalled);
    },
    onLastUnsubscribe: () => {
      window.removeEventListener('beforeinstallprompt', handleInstallPrompt);
      window.removeEventListener('appinstalled', handleInstalled);
    },
  },
);

const update = (next: Partial<TInstallPromptSnapshot>): void => {
  const snapshot = installPromptStore.getState();

  installPromptStore.setState({ ...snapshot, ...next });
};

const handleInstallPrompt = (event: Event): void => {
  event.preventDefault();
  update({ prompt: event as TBeforeInstallPromptEvent });
};

const handleInstalled = (): void => {
  update({ prompt: undefined, isInstalled: true });
  installedListeners.forEach((listener) => listener());
};

const subscribeToNothing = (): (() => void) => () => {};

const getIsStandalone = (): boolean =>
  window.matchMedia(STANDALONE_MEDIA_QUERY).matches ||
  Boolean((window.navigator as TStandaloneNavigator).standalone);

export const usePwaInstall = ({
  isEnabled = true,
  onInstalled,
}: TPwaInstallOptions = {}): TPwaInstall => {
  const { prompt, isInstalled } = useSyncExternalStore(
    isEnabled ? installPromptStore.subscribe : subscribeToNothing,
    installPromptStore.getState,
  );

  const onInstalledRef = useLatest(onInstalled);

  const promptInstall = useCallback(async () => {
    const { prompt: pendingPrompt } = installPromptStore.getState();

    if (!pendingPrompt) {
      return;
    }

    update({ prompt: undefined });
    await pendingPrompt.prompt();
    await pendingPrompt.userChoice;
  }, []);

  useEffect(() => {
    if (!isEnabled) {
      return;
    }

    const reportInstalled = () => {
      onInstalledRef.current?.();
    };

    installedListeners.add(reportInstalled);

    return () => {
      installedListeners.delete(reportInstalled);
    };
  }, [isEnabled]);

  const hasPrompt = Boolean(prompt);

  return {
    platform: getPlatform(hasPrompt, window.navigator),
    isStandalone: isInstalled || getIsStandalone(),
    promptInstall,
  };
};
